import { useState } from 'preact/hooks';
import { tryPaymentRequest } from '../core';
import type { Contact } from '../app/model';
import { PlainButton, Sheet } from './components';
import { Clipboard, People, Plus, QRScan, Trash } from './icons';
import { ui, useApp } from './state';

export function ContactRow({ contact }: { contact: Contact }) {
  return (
    <span class="contact-row">
      <span class="avatar">{contact.name.slice(0, 1).toUpperCase()}</span>
      <span class="grow min0">
        <span class="strong block">{contact.name}</span>
        <span class="mono small dim ellipsis-middle block">{contact.address}</span>
      </span>
    </span>
  );
}

export function ContactsSheet({ pick }: { pick?: (c: Contact) => void }) {
  const { s, model } = useApp();
  const [q, setQ] = useState('');
  const list = [...model.contacts]
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((c) => !q.trim() || c.name.toLowerCase().includes(q.trim().toLowerCase()) || c.address.includes(q.trim().toLowerCase()));
  return (
    <Sheet title={s.contactsTitle}
      left={<PlainButton onClick={() => ui.close()}>{pick ? s.cancel : `‹ ${s.settings}`}</PlainButton>}
      right={<PlainButton aria-label={s.contactAdd} onClick={() => ui.open({ type: 'contactEdit', contact: { id: crypto.randomUUID(), name: '', address: '' } })}><Plus /></PlainButton>}>
      {model.contacts.length === 0 ? (
        <div class="empty">
          <People size={40} />
          <p class="dim text-center">{s.contactsEmpty}</p>
        </div>
      ) : (
        <>
          <input class="search" type="search" placeholder={s.contactsSearch} value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
          <div class="group-body">
            {list.map((c, i) => (
              <>
                <button type="button" class="row row-button" onClick={() => {
                  if (pick) {
                    ui.close();
                    pick(c);
                  } else ui.open({ type: 'contactEdit', contact: c });
                }}>
                  <ContactRow contact={c} />
                </button>
                {i < list.length - 1 && <div class="divider inset" />}
              </>
            ))}
          </div>
        </>
      )}
    </Sheet>
  );
}

export function ContactEditSheet({ contact }: { contact: Contact }) {
  const { s, model } = useApp();
  const isNew = !model.contacts.some((c) => c.id === contact.id);
  const [name, setName] = useState(contact.name);
  const [address, setAddress] = useState(contact.address);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const parsed = tryPaymentRequest(address)?.address ?? null;
  const duplicate = parsed ? model.contacts.find((c) => c.address === parsed.string && c.id !== contact.id) : undefined;
  const canSave = name.trim() !== '' && parsed !== null && !duplicate;

  return (
    <Sheet title={isNew ? s.contactNew : s.contactEdit}
      left={<PlainButton onClick={() => ui.close()}>{s.cancel}</PlainButton>}
      right={<PlainButton class="strong" disabled={!canSave} onClick={async () => {
        await model.saveContact({ id: contact.id, name: name.trim(), address: parsed!.string });
        ui.close();
      }}>{s.save}</PlainButton>}>
      <section class="group">
        <div class="group-title">{s.contactName}</div>
        <div class="group-body">
          <input class="row-input" placeholder={s.contactNamePlaceholder} value={name} autocapitalize="words"
            onInput={(e) => setName((e.target as HTMLInputElement).value)} />
        </div>
      </section>
      <section class="group">
        <div class="group-title">{s.contactAddress}</div>
        <div class="group-body">
          <div class="row input-row">
            <textarea rows={2} class="mono address-input" placeholder={s.addressPlaceholder} value={address}
              autocapitalize="off" autocomplete="off" spellcheck={false}
              onInput={(e) => setAddress((e.target as HTMLTextAreaElement).value)} />
            <PlainButton aria-label={s.paste} onClick={async () => { try { setAddress((await navigator.clipboard.readText()).trim()); } catch {} }}><Clipboard size={20} /></PlainButton>
            <PlainButton aria-label={s.scanQR} onClick={() => ui.open({
              type: 'scanner', onCode: (code) => {
                const r = tryPaymentRequest(code);
                if (r) {
                  setAddress(r.address.string);
                  if (!name.trim() && r.label) setName(r.label);
                } else setAddress(code);
              },
            })}><QRScan size={20} /></PlainButton>
          </div>
        </div>
        <div class="group-footer">
          {duplicate ? <span class="error">{s.contactDuplicate(duplicate.name)}</span>
            : address && !parsed ? <span class="error">{s.addressInvalid}</span> : null}
        </div>
      </section>
      {!isNew && (
        <div class="group-body">
          {confirmDelete ? (
            <button type="button" class="row row-button danger" onClick={async () => { await model.deleteContact(contact); ui.close(); }}>
              <Trash size={18} /> <span class="row-label">{s.contactDeleteConfirm(contact.name)}</span>
            </button>
          ) : (
            <button type="button" class="row row-button danger" onClick={() => setConfirmDelete(true)}>
              <span class="row-label">{s.contactDelete}</span>
            </button>
          )}
        </div>
      )}
    </Sheet>
  );
}
